import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Button, Badge, Tabs, Tab, Table, Form, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import "../assets/css/dataTable.css";
import { AccountContext } from "components/Account/Account";
import ModalCargando from "components/Modal/ModalCargando";
import ModalError from "components/Modal/ModalError";
import downloadCSV from "../csv/downloadCSV";
import {
  obtenerPanel, obtenerPlanDetalle, buscarReceptor,
  crearJobDescarga, getJobDescarga,
  crearJobPlan, getJobPlan,
  crearJobAplicar, getJobAplicar,
  reanudarBreaker, setAutomatico,
} from "api/padronSii";

const AMBIENTES = [
  { valor: "palena", etiqueta: "Producción (palena)", boton: "Ejecutar incremental" },
  { valor: "maullin", etiqueta: "Certificación (maullin)", boton: "Certificación completa" },
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_INTENTOS = 200; // ~16 minutos a 5s por consulta, igual que CheckDocumentosCargadosDesdeHasta

// En pantallas anchas el contenido se estiraba de borde a borde: cinco tarjetas
// de anchos distintos, el aviso desplegado a dos metros del botón que lo
// resuelve y media pantalla vacía. Se acota el ancho de lectura y se centra.
const ANCHO_MAXIMO = 1400;

// La paleta y la escala son las del tema del monitor
// (light-bootstrap-dashboard-react.css), no unas propias: esta vista tiene que
// verse parte de la aplicación y no una isla. Los valores salen de ahí —
// `.btn-*` para los semánticos, `.card label` para las etiquetas (12px
// mayúsculas #9A9A9A) y `.card-title` para los títulos (peso 300, #333).
const TEMA = {
  primary: "#3472F7",
  success: "#87CB16",
  info: "#1DC7EA",
  warning: "#FF9500",
  danger: "#FF4A55",
  tinta: "#333333",
  suave: "#9A9A9A",
  borde: "#DDDDDD",
};

const ETIQUETA = {
  fontSize: "12px",
  textTransform: "uppercase",
  color: TEMA.suave,
  fontWeight: 400,
  letterSpacing: "0.02em",
  marginBottom: "2px",
};

const VALOR = { fontSize: "22px", fontWeight: 300, color: TEMA.tinta, lineHeight: 1.4 };
const AYUDA = { fontSize: "12px", color: TEMA.suave, lineHeight: 1.5 };

const RESULTADO_BADGE = {
  ok: "success",
  error: "danger",
  bloqueado_guardas: "warning",
  bloqueado_breaker: "danger",
  csv_regresivo: "warning",
  pending: "secondary",
  running: "info",
};

function badgeResultado(resultado) {
  const variant = RESULTADO_BADGE[resultado] || "secondary";
  return <Badge bg={variant}>{resultado}</Badge>;
}

// Severidad del backend -> color del cuadro. Mismo vocabulario que usa el
// correo del parte diario.
//
// Con las clases `alert-*` del tema el aviso quedaba ilegible: fondo saturado
// con texto claro encima, y el botón de acción perdido dentro del mismo bloque
// de color. Paleta propia: fondo suave, franja de color a la izquierda y texto
// oscuro, que es como se leen los avisos en el resto del monitor.
const PALETA_AVISO = {
  critica: { fondo: "#FFEDEE", borde: TEMA.danger, texto: "#B3333C" },
  alta: { fondo: "#FFF4E5", borde: TEMA.warning, texto: "#A66100" },
  media: { fondo: "#E9F8FC", borde: TEMA.info, texto: "#12798F" },
  info: { fondo: "#F4F5F7", borde: TEMA.suave, texto: "#666666" },
};

function estiloAviso(severidad) {
  const p = PALETA_AVISO[severidad] || PALETA_AVISO.info;
  return {
    background: p.fondo,
    borderLeft: `4px solid ${p.borde}`,
    color: p.texto,
    borderRadius: "0 6px 6px 0",
    padding: "12px 16px",
    marginBottom: "12px",
  };
}

// Avisos que hablan del plan pendiente y llevan su planId al lado.
const ACCION_SOBRE_PLAN = ["sin_aplicar", "guardas_rojas"];

// Qué mide cada guarda, en palabras. El `detalle` que manda el backend trae el
// número; esto explica por qué ese número importa, que es lo que no se entendía
// mirando "bajas: OK" a secas.
const QUE_MIDE_LA_GUARDA = {
  bajas: "Cuánta gente saldría del padrón de una sola vez. Un salto grande casi nunca son bajas reales: es un CSV incompleto.",
  cuarentena: "Cuántos receptores del CSV vienen sin una casilla de intercambio utilizable.",
  merma: "Si el CSV trae menos filas que la tabla. Detecta una descarga cortada a la mitad.",
  regresion: "Si el CSV parece más viejo que la tabla: no trae ninguna alta y aun así daría de baja o reactivaría gente. Es lo que pasó el 09-09-2026, cuando el SII sirvió el archivo de una semana antes.",
};

const num = (n) => (n == null ? "—" : Number(n).toLocaleString("es-CL"));
const fecha = (iso) => (iso ? iso.replace("T", " ").slice(0, 16) : "—");

// "2.219 altas · 372 bajas · 1.295 cambios", que es lo que hay que mirar antes
// de decidir si se aplica.
function resumenCorto(r) {
  if (!r) return "sin resumen";
  const partes = [`${num(r.altas)} altas`, `${num(r.bajas)} bajas`, `${num(r.cambios)} cambios`];
  if (r.reactivaciones) partes.push(`${num(r.reactivaciones)} reactivaciones`);
  return partes.join(" · ");
}

// ---------------------------------------------------------------------------
// Una tarjeta de estado. Todas del mismo alto y del mismo ancho: la fila de
// KPI se lee de un vistazo solo si son comparables entre sí.
// ---------------------------------------------------------------------------
function Kpi({ titulo, ayuda, valor, children }) {
  return (
    <Card className="h-100 mb-0">
      <Card.Body className="py-3">
        <div style={ETIQUETA} title={ayuda}>{titulo}</div>
        <div style={VALOR}>{valor}</div>
        {children}
      </Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Qué hace el módulo y qué toca, dibujado. Tres actos: los dos primeros los
// dispara el cron y no escriben en la base; el tercero lo aprieta una persona y
// es el único que la modifica. Esa distinción es el corazón del diseño y hasta
// ahora había que contarla de memoria.
// ---------------------------------------------------------------------------
function DiagramaFlujo() {
  // Los mismos colores que el resto del monitor: azul del tema para lo que
  // corre solo, verde para el acto humano. El verde del tema (#87CB16) es muy
  // claro para texto chico, así que ahí se usa un tono propio más oscuro.
  const AUTO = TEMA.primary;
  const HUMANO = TEMA.success;
  const HUMANO_TEXTO = "#6A9E12";
  const GRIS = TEMA.suave;
  const TINTA = TEMA.tinta;

  const paso = (x, color, colorTexto, nro, titulo, cuando) => (
    <g>
      <rect x={x} y="18" width="196" height="62" rx="8" fill="#fff" stroke={color} strokeWidth="2" />
      <circle cx={x + 22} cy="42" r="11" fill={color} />
      <text x={x + 22} y="46" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">{nro}</text>
      <text x={x + 40} y="46" fill={TINTA} fontSize="13.5" fontWeight="600">{titulo}</text>
      <text x={x + 40} y="65" fill={colorTexto} fontSize="11.5">{cuando}</text>
    </g>
  );

  const flecha = (x) => (
    <g>
      <line x1={x} y1="49" x2={x + 30} y2="49" stroke={GRIS} strokeWidth="1.5" />
      <polygon points={`${x + 30},49 ${x + 23},45 ${x + 23},53`} fill={GRIS} />
    </g>
  );

  const toca = (x, lineas) => (
    <g>
      {lineas.map((l, i) => (
        <text key={i} x={x} y={106 + i * 17} fill={i === 0 ? GRIS : TINTA} fontSize="11.5">{l}</text>
      ))}
    </g>
  );

  return (
    <svg viewBox="0 0 940 208" width="100%" style={{ maxWidth: 940, display: "block" }} role="img"
      aria-label="Flujo del módulo: descargar, planificar y aplicar">
      {paso(6, AUTO, AUTO, "1", "Descargar", "cron 11:00 UTC · automático")}
      {flecha(202)}
      {paso(238, AUTO, AUTO, "2", "Planificar", "cron 11:20 UTC · automático")}
      {flecha(434)}
      {paso(470, HUMANO, HUMANO_TEXTO, "3", "Aplicar", "solo si una persona lo aprieta")}

      {toca(6, ["toca:", "SII (una sesión, se cierra)", "S3: guarda el CSV + sha256"])}
      {toca(238, ["toca:", "S3: lee el CSV, escribe el plan", "Postgres: SOLO SELECT"])}
      {toca(470, ["escribe:", "padron_receptores_sii", "padron_cambios (changelog)"])}

      {/* La cuarta pieza no es un paso del pipeline: mira lo que quedó y avisa. */}
      <rect x="700" y="18" width="234" height="62" rx="8" fill="#fff" stroke={GRIS} strokeWidth="1.5" strokeDasharray="4 3" />
      <text x="716" y="42" fill={TINTA} fontSize="13.5" fontWeight="600">Parte diario</text>
      <text x="716" y="61" fill={GRIS} fontSize="11.5">cron 11:50 UTC · correo por SES</text>
      {toca(700, ["mira:", "el estado de arriba y avisa", "lo mismo que muestra este panel"])}

      <line x1="6" y1="178" x2="934" y2="178" stroke="#e3e3e3" strokeWidth="1" />
      <text x="6" y="196" fill={GRIS} fontSize="11.5">
        Nada se escribe en la base hasta el paso 3. Los pasos 1 y 2 se pueden repetir sin consecuencias.
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Las guardas, con lo que miden y lo que hicieron. Antes era una línea por
// guarda que decía "OK" y un número sin contexto.
// ---------------------------------------------------------------------------
function TablaGuardas({ guardas }) {
  if (!guardas || !guardas.length) return null;
  const hayRojas = guardas.some((g) => !g.ok);
  return (
    <div className="mb-3">
      <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
        Guardas — controles que se corren sobre el plan antes de dejarlo aplicar
      </div>
      {/* Lista y no tabla: el detalle de cada guarda es una frase con cifras y
          esto vive en la columna angosta del detalle. Cuatro columnas obligaban
          a cortar el texto justo donde está el número que importa. */}
      <div style={{ border: `1px solid ${TEMA.borde}`, borderRadius: "6px", overflow: "hidden" }}>
        {guardas.map((g, i) => (
          <div
            key={g.nombre}
            style={{
              padding: "9px 12px",
              borderTop: i === 0 ? "none" : `1px solid ${TEMA.borde}`,
              background: g.ok ? "transparent" : PALETA_AVISO.critica.fondo,
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <Badge bg={g.ok ? "success" : "danger"}>{g.ok ? "pasó" : "rojo"}</Badge>
              <span style={{ fontSize: "13px", fontWeight: 500, color: TEMA.tinta, textTransform: "capitalize" }}>{g.nombre}</span>
              <span style={{ fontSize: "12px", color: TEMA.suave }}>{g.ok ? "dejó seguir" : "bloqueó la aplicación"}</span>
            </div>
            <div style={{ fontSize: "12.5px", color: TEMA.tinta, marginTop: "3px" }}>{g.detalle}</div>
            <div style={{ ...AYUDA, marginTop: "2px" }}>{QUE_MIDE_LA_GUARDA[g.nombre] || ""}</div>
          </div>
        ))}
      </div>
      <div style={{ ...AYUDA, marginTop: "8px" }}>
        {hayRojas
          ? "Con una guarda en rojo el plan no se aplica: hay que revisarlo y, si de verdad corresponde, forzarlo a mano."
          : "Todas pasaron, así que este plan se puede aplicar por el camino normal."}
      </div>
    </div>
  );
}

function PadronSii() {
  const { getSession } = useContext(AccountContext);

  const [ambiente, setAmbiente] = useState("palena");
  const [panel, setPanel] = useState(null);
  const [cargandoPanel, setCargandoPanel] = useState(false);

  const [filter, setFilter] = useState("");
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [planDetalle, setPlanDetalle] = useState(null);
  const [tab, setTab] = useState("detalle");
  const [verComoFunciona, setVerComoFunciona] = useState(false);

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const cfgAmbiente = useMemo(() => AMBIENTES.find((a) => a.valor === ambiente), [ambiente]);

  // Dos banderas independientes, no una:
  //   cronActivo      -> interruptor del operador; solo gobierna la corrida diaria.
  //   breakerAbierto  -> reacción a 3 errores; bloquea el cron Y lo manual.
  // Los estados guardados antes del 07-09-2026 no traen `cron_activo`; ausente
  // se lee como prendido, que es como se comportaban.
  const breaker = panel?.breaker;
  const cronActivo = breaker ? breaker.cron_activo !== false : true;
  const breakerAbierto = breaker?.modo === "manual";
  const apagadoPor = breaker?.cron_cambiado_by
    ? `${breaker.cron_cambiado_by}${breaker.cron_cambiado_at ? ` el ${breaker.cron_cambiado_at.replace("T", " ").slice(0, 16)}` : ""}`
    : null;

  // Lo pendiente lo calcula el backend (GET /padronSii/panel), no esta vista:
  // la misma regla la va a usar la alerta por correo.
  const pendiente = panel?.pendiente;
  const avisos = panel?.avisos || [];

  // Qué planes ya se aplicaron, para marcarlos en el historial. El backend
  // manda el índice completo, así que se resuelve acá sin otra llamada.
  const planesAplicados = useMemo(() => new Set(
    (panel?.indice || [])
      .filter((x) => x.tipo === "aplicar" && x.resultado === "ok" && x.planId)
      .map((x) => x.planId)), [panel]);

  const cargarPanel = useCallback(async (amb) => {
    setCargandoPanel(true);
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await obtenerPanel(idToken, amb);
      setPanel(resp.data);
    } catch (error) {
      console.error(error);
    } finally {
      setCargandoPanel(false);
    }
  }, [getSession]);

  useEffect(() => { cargarPanel(ambiente); setFilaSeleccionada(null); setPlanDetalle(null); }, [ambiente, cargarPanel]);

  // Polling genérico: espera a que un job deje de estar pending/running.
  const esperarJob = async (idToken, getJobFn, jobId, mensajeFn) => {
    for (let i = 0; i < MAX_INTENTOS; i++) {
      await sleep(5000);
      const resp = await getJobFn(idToken, jobId);
      const job = resp.data;
      if (!["pending", "running"].includes(job.status)) return job;
      if (mensajeFn) setLoadingMessage(mensajeFn(job));
    }
    throw new Error("Tiempo de espera agotado.");
  };

  // "Ejecutar incremental" / "Certificación completa": descargar -> plan,
  // orquestado desde el frontend (no una lambda invocando a la otra).
  const ejecutarIncremental = async () => {
    setShowLoading(true);
    setLoadingMessage(" Iniciando descarga...");
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);

      const jobDescarga = await crearJobDescarga(idToken, { ambiente });
      const resultDescarga = await esperarJob(idToken, getJobDescarga, jobDescarga.data.jobId,
        () => " Descargando el padrón del SII...");
      if (resultDescarga.status === "bloqueado_breaker") {
        throw new Error(resultDescarga.error || "Bloqueado por el circuit breaker.");
      }
      if (resultDescarga.status !== "done") {
        throw new Error(resultDescarga.error || "La descarga terminó con error.");
      }

      setLoadingMessage(" Planificando (diff contra la base)...");
      const jobPlan = await crearJobPlan(idToken, { ambiente, csvKey: resultDescarga.resultado.csvKey });
      const resultPlan = await esperarJob(idToken, getJobPlan, jobPlan.data.jobId,
        () => " Planificando (diff contra la base)...");
      if (resultPlan.status === "error") throw new Error(resultPlan.error || "El plan terminó con error.");

      setShowLoading(false);
      await cargarPanel(ambiente);
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(error.message || "Error inesperado.");
      setShowErrorModal(true);
    }
  };

  const aplicarPlan = async (planId, forzar = false) => {
    setShowLoading(true);
    setLoadingMessage(" Aplicando el plan...");
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const job = await crearJobAplicar(idToken, { planId, forzar });
      const resultado = await esperarJob(idToken, getJobAplicar, job.data.jobId, () => " Aplicando el plan...");
      if (resultado.status === "error") throw new Error(resultado.error || "Error al aplicar.");
      setShowLoading(false);
      if (resultado.status === "bloqueado_guardas" && !forzar) {
        setErrorMessage("Hay guardas en rojo. Revisar el detalle antes de forzar la aplicación.");
        setShowErrorModal(true);
      }
      await cargarPanel(ambiente);
      setFilaSeleccionada(null);
      setPlanDetalle(null);
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(error.message || "Error inesperado.");
      setShowErrorModal(true);
    }
  };

  const onReanudar = async () => {
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      await reanudarBreaker(idToken, { ambiente });
      await cargarPanel(ambiente);
    } catch (error) {
      setErrorMessage(error.message || "Error al reanudar.");
      setShowErrorModal(true);
    }
  };

  // Prende/apaga SOLO la corrida diaria. Apagarla no toca el circuit breaker ni
  // deshabilita el botón "Ejecutar incremental": el sentido de apagarla es
  // decidir uno cuándo corre.
  const onToggleAutomatico = async () => {
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      await setAutomatico(idToken, { ambiente, activo: !cronActivo });
      await cargarPanel(ambiente);
    } catch (error) {
      setErrorMessage(error.message || "Error al cambiar la corrida automática.");
      setShowErrorModal(true);
    }
  };

  const seleccionarFila = async (fila) => {
    setFilaSeleccionada(fila);
    setPlanDetalle(null);
    setTab("detalle");
    if (!fila.planId) return;
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await obtenerPlanDetalle(idToken, fila.planId);
      setPlanDetalle(resp.data);
    } catch (error) {
      console.error(error);
    }
  };

  // CSV para revisar el plan afuera antes de aplicarlo: un renglón por cada
  // cambio concreto, con el valor anterior y el nuevo al lado.
  //
  // Todas las filas llevan las mismas columnas a propósito: downloadCSV arma el
  // encabezado con las claves de la PRIMERA fila, así que mientras el detalle de
  // los cambios tuvo una columna propia bastaba con que hubiera un alta arriba
  // para que ese dato no llegara nunca al archivo.
  const exportarDetalleCSV = () => {
    if (!planDetalle) return;
    const fila = (accion, f, campo, anterior, nuevo) => ({
      plan_id: planDetalle.plan_id,
      accion,
      rut: f.rut ?? "",
      // Las reactivaciones guardan la fila entrante anidada en `fila`, y los
      // planes anteriores al 08-09-2026 no traen razón social en los cambios:
      // en ese caso la columna queda vacía, como venía.
      razon_social: f.razon_social ?? f.fila?.razon_social ?? "",
      campo: campo ?? "",
      anterior: anterior ?? "",
      nuevo: nuevo ?? "",
    });
    const filas = [
      ...(planDetalle.altas || []).map((f) => fila("alta", f)),
      ...(planDetalle.bajas || []).map((f) => fila("baja", f)),
      ...(planDetalle.reactivaciones || []).map((f) => fila("reactivacion", f)),
      ...(planDetalle.cambios || []).flatMap((c) =>
        (c.campos || []).map((x) => fila("cambio", c, x.campo, x.anterior, x.nuevo))),
    ];
    if (!filas.length) return;
    downloadCSV(filas, `padron_${planDetalle.plan_id}.csv`);
  };

  const indiceFiltrado = useMemo(() => {
    const indice = panel?.indice || [];
    if (!filter) return indice;
    const f = filter.toLowerCase();
    return indice.filter((x) =>
      (x.tipo || "").toLowerCase().includes(f) ||
      (x.resultado || "").toLowerCase().includes(f) ||
      (x.planId || "").toLowerCase().includes(f));
  }, [panel, filter]);

  // Vigentes: no hay un endpoint de conteo aparte — se toma del último job
  // (plan o aplicar) que trajo un número, que ya viaja en el índice. Un plan
  // sin aplicar todavía trae vigentesActuales (lo que había ANTES de ese
  // plan); un aplicar trae filasVigentes (lo que quedó DESPUÉS). El más
  // reciente de los dos es la mejor estimación disponible sin agregar una
  // consulta nueva a la base solo para esto.
  const vigentes = useMemo(() => {
    const conDato = (panel?.indice || []).find((x) => x.resumen && (x.resumen.filasVigentes ?? x.resumen.vigentesActuales) != null);
    if (!conDato) return null;
    return conDato.resumen.filasVigentes ?? conDato.resumen.vigentesActuales;
  }, [panel]);

  // Altas/bajas/cambios separados en vez de una suma: la suma escondía justo lo
  // que se necesita para decidir (300 bajas no es lo mismo que 300 altas).
  const columnas = [
    // Sin `wrap`: la fecha completa entra en un renglon y partirla en dos
    // ("2026-09-09" arriba, "11:20:39" abajo) hacia la tabla mas alta y mas
    // dificil de recorrer con la vista.
    { name: "Fecha", selector: (row) => row.terminadoAt || row.iniciadoAt, sortable: true, minWidth: "165px",
      style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
      cell: (row) => (row.terminadoAt || row.iniciadoAt || "").replace("T", " ").slice(0, 19) },
    { name: "Tipo", selector: (row) => row.tipo, sortable: true, width: "115px",
      cell: (row) => (
        <>
          <Badge bg="info" text="dark">{row.tipo}</Badge>
          {row.tipo === "plan" && row.planId && row.resultado === "ok" && !planesAplicados.has(row.planId) && (
            <Badge bg="warning" text="dark" className="ms-1" title="Este plan nunca se aplicó.">sin aplicar</Badge>
          )}
        </>
      ) },
    // Los anchos fijos de 80px partían los números en dos renglones ("1 748.0
    // 00") apenas el padrón completo entraba como altas. Se dan por contenido,
    // sin cortar, y alineados a la derecha para poder compararlos de un vistazo.
    { name: "Altas", right: true, minWidth: "96px", style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }, cell: (row) => num(row.resumen?.altas) },
    { name: "Bajas", right: true, minWidth: "88px", style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }, cell: (row) => num(row.resumen?.bajas) },
    { name: "Cambios", right: true, minWidth: "96px", style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }, cell: (row) => num(row.resumen?.cambios) },
    { name: "Resultado", minWidth: "130px", cell: (row) => badgeResultado(row.resultado) },
  ];

  const estadoGeneral = breakerAbierto
    ? { texto: "Bloqueado", color: "danger", nota: "el circuit breaker está abierto" }
    : cronActivo
      ? { texto: "En automático", color: "success", nota: "el cron descarga y planifica solo" }
      : { texto: "Solo manual", color: "secondary", nota: "el cron no dispara; el botón sí funciona" };

  return (
    <>
      <div style={{ maxWidth: ANCHO_MAXIMO, margin: "0 auto" }}>
        {/* ---- Encabezado: qué es esto, y sobre qué ambiente estoy mirando ---- */}
        <Card className="mb-3">
          <Card.Body className="py-3">
            <Row className="align-items-center g-2">
              <Col md="7">
                <Card.Title as="h4" className="mb-1">Padrón SII</Card.Title>
                <div style={{ fontSize: "13px", color: TEMA.suave, lineHeight: 1.55 }}>
                  Nómina oficial de receptores electrónicos del SII. De acá sale la casilla
                  a la que se le manda cada factura, así que el padrón viejo es factura mal enviada.
                </div>
              </Col>
              <Col md="5">
                <div className="d-flex align-items-center justify-content-md-end" style={{ gap: "10px" }}>
                  <span className="text-muted small flex-shrink-0">Ambiente</span>
                  <Form.Select
                    value={ambiente}
                    onChange={(e) => setAmbiente(e.target.value)}
                    style={{ maxWidth: 260 }}
                  >
                    {AMBIENTES.map((a) => <option key={a.valor} value={a.valor}>{a.etiqueta}</option>)}
                  </Form.Select>
                </div>
                <div className="text-md-end mt-1" style={AYUDA}>
                  {cargandoPanel
                    ? "cargando…"
                    : panel
                      ? <>base <strong>{ambiente}</strong> · última descarga {fecha(panel.ultimaDescarga?.descargado_at)}</>
                      : ""}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ---- Avisos. Los evalúa el backend, y son exactamente los mismos que
             sale a decir el parte diario por correo: así el panel y el correo no
             pueden contarle cosas distintas a la misma persona. Acá se les
             agrega lo único que un correo no puede tener, que es el botón para
             resolverlos. ---- */}
        {avisos.map((a) => (
          <div key={a.codigo} style={estiloAviso(a.severidad)} className="d-flex justify-content-between align-items-center gap-3">
            <div>
              <strong>{a.titulo}</strong>
              <div className="small">{a.detalle}</div>
              {ACCION_SOBRE_PLAN.includes(a.codigo) && pendiente?.plan && (
                <div className="small text-monospace" style={{ opacity: 0.75 }}>{pendiente.plan.planId}</div>
              )}
            </div>
            <div className="flex-shrink-0">
              {a.codigo === "breaker_abierto" && (
                <Button variant="success" size="sm" className="btn-fill" onClick={onReanudar}>Reanudar</Button>
              )}
              {a.codigo === "sin_aplicar" && pendiente?.plan && (
                <Button
                  variant="success" size="sm" className="btn-fill"
                  onClick={() => aplicarPlan(pendiente.plan.planId, false)}
                  disabled={showLoading || breakerAbierto}
                >
                  Aplicar ahora
                </Button>
              )}
            </div>
          </div>
        ))}

        {/* Un plan del día todavía sin aplicar no es un aviso —el atraso se
            cuenta en días— pero el botón tiene que estar a mano igual. */}
        {pendiente?.hayPlanSinAplicar && !avisos.some((a) => ACCION_SOBRE_PLAN.includes(a.codigo)) && (
          <div style={estiloAviso("media")} className="d-flex justify-content-between align-items-center gap-3">
            <div>
              <strong>Plan del día sin aplicar</strong> — {resumenCorto(pendiente.plan.resumen)}.
              <div className="small text-monospace" style={{ opacity: 0.75 }}>{pendiente.plan.planId}</div>
            </div>
            <Button
              variant="success" size="sm" className="btn-fill flex-shrink-0"
              onClick={() => aplicarPlan(pendiente.plan.planId, false)}
              disabled={showLoading || breakerAbierto || pendiente.plan.guardasOk === false}
            >
              Aplicar ahora
            </Button>
          </div>
        )}

        {/* ---- Estado, en cuatro tarjetas iguales ---- */}
        <Row className="g-3 mb-3">
          <Col md="3">
            <Kpi
              titulo="Receptores vigentes"
              ayuda="Receptores activos según la última corrida (plan o aplicar) — no es una cuenta en vivo."
              valor={vigentes != null ? vigentes.toLocaleString("es-CL") : "—"}
            >
              <div style={AYUDA}>en la base de {ambiente}</div>
            </Kpi>
          </Col>
          <Col md="3">
            <Kpi
              titulo="Última descarga"
              ayuda="Cuándo se bajó por última vez el CSV del SII. La descarga sola no cambia nada en la base."
              valor={fecha(panel?.ultimaDescarga?.descargado_at)}
            >
              <div className="text-truncate" title={panel?.ultimaDescarga?.archivo || ""} style={AYUDA}>
                {panel?.ultimaDescarga?.archivo || "—"}
              </div>
            </Kpi>
          </Col>
          <Col md="3">
            <Kpi
              titulo="Última aplicación"
              ayuda="Cuándo se escribió por última vez en padron_receptores_sii. Es la fecha que dice qué tan viejo está el padrón de verdad."
              valor={
                <>
                  {fecha(pendiente?.ultimaAplicacion?.at)}
                  {pendiente?.ultimaAplicacion?.dias > 1 && (
                    <Badge bg={pendiente.ultimaAplicacion.dias >= 3 ? "warning" : "secondary"} text="dark" className="ms-2" style={{ fontSize: "11px", verticalAlign: "middle" }}>
                      hace {pendiente.ultimaAplicacion.dias} días
                    </Badge>
                  )}
                </>
              }
            >
              {/* Sin ninguna aplicación previa el KPI ya dice «—»; agregarle
                  «sin resumen» debajo sugiere que hubo una aplicación de la que
                  falta el detalle, que es otra cosa. */}
              {pendiente?.ultimaAplicacion && (
                <div style={AYUDA}>{resumenCorto(pendiente.ultimaAplicacion.resumen)}</div>
              )}
            </Kpi>
          </Col>
          <Col md="3">
            <Kpi
              titulo="Estado del módulo"
              ayuda="Resume las dos banderas: el interruptor del operador (automático) y el circuit breaker (reacción a errores)."
              valor={<Badge bg={estadoGeneral.color} style={{ fontSize: "13px", fontWeight: 500 }}>{estadoGeneral.texto}</Badge>}
            >
              <div style={AYUDA}>{estadoGeneral.nota}</div>
              <div style={AYUDA}>
                circuit breaker {breaker ? `${breaker.errores_consecutivos}/3` : "—"} · {breaker ? (breakerAbierto ? "abierto" : "cerrado") : "—"}
              </div>
            </Kpi>
          </Col>
        </Row>

        {/* ---- Controles, todos juntos y explicados ---- */}
        <Card className="mb-3">
          <Card.Body className="py-3">
            <Row className="g-3 align-items-start">
              <Col md="5">
                <div style={ETIQUETA}>
                  Piloto automático
                </div>
                {/* Nada de <Form.Check type="switch">: este proyecto tiene
                    Bootstrap 4.6 con react-bootstrap 2.x, que emite el markup de
                    Bootstrap 5 (.form-switch) para el que el tema no trae NINGUNA
                    regla. El interruptor no se dibujaba —quedaba el hueco de 35px
                    del label y el texto capitalizado por el tema—, así que el
                    mando principal del módulo era invisible. Un estado + un botón
                    que dice qué va a pasar no depende de ese CSS y además es
                    menos ambiguo que un switch. */}
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Badge bg={cronActivo ? "success" : "secondary"} style={{ fontSize: "12px", fontWeight: 500 }}>
                    {cronActivo ? "Prendido" : "Apagado"}
                  </Badge>
                  <Button
                    size="sm"
                    variant={cronActivo ? "outline-secondary" : "success"}
                    onClick={onToggleAutomatico}
                    disabled={cargandoPanel || !panel}
                  >
                    {cronActivo ? "Apagar" : "Prender"}
                  </Button>
                  <span style={AYUDA}>
                    {cronActivo ? "descarga y planifica solo, todos los días" : "no dispara nada por su cuenta"}
                  </span>
                </div>
                <div style={AYUDA}>
                  Prende y apaga <strong>solo la corrida diaria</strong>. Apagarlo no bloquea el botón de acá al
                  lado, y tampoco aplica nada: aplicar siempre lo aprieta una persona.
                  {!cronActivo && apagadoPor && <> Lo apagó {apagadoPor}.</>}
                </div>
              </Col>
              <Col md="4">
                <div style={ETIQUETA}>
                  Corrida manual
                </div>
                <Button variant="primary" className="btn-fill" onClick={ejecutarIncremental} disabled={showLoading || breakerAbierto}>
                  {cfgAmbiente.boton}
                </Button>
                <div className="mt-1" style={AYUDA}>
                  Descarga el padrón y arma el plan. <strong>No escribe en la base</strong>: deja el plan listo para revisar.
                  {breakerAbierto && <> Hoy está bloqueado por el circuit breaker.</>}
                </div>
              </Col>
              <Col md="3">
                <div style={ETIQUETA}>
                  Circuit breaker
                </div>
                <div>
                  <Badge bg={breakerAbierto ? "danger" : "success"}>{breakerAbierto ? "abierto" : "cerrado"}</Badge>
                  {breakerAbierto && (
                    <Button variant="success" size="sm" className="btn-fill ms-2" onClick={onReanudar}>Reanudar</Button>
                  )}
                </div>
                <div className="mt-1" style={AYUDA}>
                  Se abre solo, al tercer error seguido, y ahí bloquea todo —cron y botón— hasta que alguien lo reanude.
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ---- Cómo funciona: plegado, para que no estorbe al que ya lo sabe ---- */}
        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center gap-3">
              <div style={{ fontSize: "13px", color: TEMA.suave }}>
                Cómo funciona y qué toca cada paso — los tres actos, cuál es automático y cuál lo aprieta una persona
              </div>
              <Button variant="outline-primary" size="sm" className="flex-shrink-0" onClick={() => setVerComoFunciona((v) => !v)}>
                {verComoFunciona ? "Ocultar" : "Ver el diagrama"}
              </Button>
            </div>
            {verComoFunciona && (
              <div className="mt-3">
                <DiagramaFlujo />
              </div>
            )}
          </Card.Body>
        </Card>

        {/* ---- Historial y detalle, uno al lado del otro y con títulos ---- */}
        <Row className="g-3 mb-3">
          <Col lg="7">
            <Card className="h-100">
              <Card.Body>
                <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
                  Historial de ejecuciones — clic en una fila para ver el detalle
                </div>
                <Form.Control
                  type="text" placeholder="Filtrar por tipo, resultado o plan…" className="mb-2"
                  value={filter} onChange={(e) => setFilter(e.target.value)}
                />
                <DataTable
                  className="-striped -highlight"
                  fixedHeader
                  columns={columnas}
                  data={indiceFiltrado}
                  pagination
                  paginationPerPage={10}
                  highlightOnHover
                  pointerOnHover
                  onRowClicked={seleccionarFila}
                  progressPending={cargandoPanel}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col lg="5">
            <Card className="h-100">
              <Card.Body>
                <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
                  Detalle del plan
                </div>
                {!filaSeleccionada && (
                  <p className="mb-0" style={AYUDA}>
                    Elegí una ejecución del historial. Si es un plan, acá aparece qué cambiaría,
                    qué dijeron las guardas y el botón para aplicarlo.
                  </p>
                )}
                {filaSeleccionada && (
                  <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
                    <Tab eventKey="detalle" title="Qué cambiaría">
                      {!planDetalle && filaSeleccionada.planId && <Spinner animation="border" size="sm" />}
                      {!filaSeleccionada.planId && <p style={AYUDA}>Esta ejecución no generó un plan (descarga, o error antes de planificar).</p>}
                      {planDetalle && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="small text-monospace text-muted">{planDetalle.plan_id}</span>
                            {planesAplicados.has(planDetalle.plan_id)
                              ? <Badge bg="success">Ya aplicado</Badge>
                              : <Badge bg="warning" text="dark">Sin aplicar</Badge>}
                          </div>
                          <Table size="sm" borderless className="mb-3" style={{ fontSize: "13px" }}>
                            <tbody>
                              <tr><td>Altas</td><td className="text-end">{num(planDetalle.resumen.altas)}</td></tr>
                              <tr><td>Bajas</td><td className="text-end">{num(planDetalle.resumen.bajas)}</td></tr>
                              <tr><td>Cambios</td><td className="text-end">{num(planDetalle.resumen.cambios)}</td></tr>
                              <tr><td>Reactivaciones</td><td className="text-end">{num(planDetalle.resumen.reactivaciones)}</td></tr>
                              <tr className="text-muted"><td>Sin cambio</td><td className="text-end">{num(planDetalle.resumen.sin_cambio)}</td></tr>
                              <tr className="text-muted"><td title="Filas que el plan deja intactas por tener un override manual.">Congelados</td><td className="text-end">{num(planDetalle.resumen.congelados)}</td></tr>
                              <tr className="text-muted"><td>Vigentes antes</td><td className="text-end">{num(planDetalle.resumen.vigentesActuales)}</td></tr>
                            </tbody>
                          </Table>

                          <TablaGuardas guardas={planDetalle.guardas} />

                          <Button
                            variant="success" size="sm" className="btn-fill"
                            onClick={() => aplicarPlan(planDetalle.plan_id, false)}
                            disabled={showLoading}
                          >
                            Aplicar
                          </Button>
                          {planDetalle.guardas.some((g) => !g.ok) && (
                            <Button
                              variant="outline-danger" size="sm" className="ms-2"
                              onClick={() => aplicarPlan(planDetalle.plan_id, true)}
                              disabled={showLoading}
                            >
                              Forzar de todos modos
                            </Button>
                          )}
                        </>
                      )}
                    </Tab>
                    <Tab eventKey="hallazgos" title="Cambios uno por uno">
                      {planDetalle && (
                        <>
                          {planDetalle.cambios.slice(0, 20).map((c, i) => (
                            <div key={i} className="small border-bottom py-1">
                              <strong>{c.rut}</strong>
                              {c.razon_social && <span className="text-muted"> · {c.razon_social}</span>}
                              <div className="text-muted">{c.campos.map((x) => `${x.campo}: ${x.anterior} → ${x.nuevo}`).join(", ")}</div>
                            </div>
                          ))}
                          <p className="small text-muted mt-2 mb-1">
                            {(planDetalle.cambios || []).length > 20
                              ? `Se muestran 20 de ${num(planDetalle.cambios.length)} cambios.`
                              : `${num((planDetalle.cambios || []).length)} cambios.`}
                            {" "}El CSV trae todo: altas, bajas, reactivaciones y cada campo con su valor anterior y el nuevo.
                          </p>
                          <Button variant="outline-secondary" size="sm" onClick={exportarDetalleCSV}>
                            Exportar detalle CSV
                          </Button>
                        </>
                      )}
                    </Tab>
                  </Tabs>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ---- Consulta puntual, al final: es la herramienta de soporte ---- */}
        <Card className="mb-3">
          <Card.Body>
            <div style={{ ...ETIQUETA, marginBottom: "8px" }}>
              Buscar un receptor en el padrón completo
            </div>
            <div className="mb-2" style={AYUDA}>
              Busca en toda la base de {ambiente}, no solo en el plan que esté seleccionado arriba.
              Sirve para responder «¿a qué casilla le estamos mandando los DTE a este RUT?».
            </div>
            <Row>
              <Col lg="8">
                <BuscadorReceptorPadron getSession={getSession} />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      <ModalCargando
        modalTitleLoading="Padrón SII"
        showLoading={showLoading}
        loadingDetail={true}
        loadingMessage={loadingMessage}
        handleClose={() => setShowLoading(false)}
      />
      <ModalError
        showTitle="Padrón SII"
        showErrorModal={showErrorModal}
        errorMessage={errorMessage}
        resetModal={() => setShowErrorModal(false)}
      />
    </>
  );
}

// Busca un receptor en el padrón COMPLETO (vía v_padron_receptores, con
// overrides aplicados) — independiente de cuál plan esté seleccionado, o de
// si hay alguno seleccionado. Reemplaza la búsqueda vieja que solo miraba el
// delta de un plan cargado en memoria.
function BuscadorReceptorPadron({ getSession }) {
  const [rut, setRut] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [noEncontrado, setNoEncontrado] = useState(false);

  const buscar = async (e) => {
    e.preventDefault();
    if (!rut) return;
    setBuscando(true);
    setResultado(null);
    setNoEncontrado(false);
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await buscarReceptor(idToken, rut);
      setResultado(resp.data);
    } catch (error) {
      if (error?.response?.status === 404) setNoEncontrado(true);
      else console.error(error);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <>
      <Form onSubmit={buscar} className="d-flex gap-2 mb-2">
        <Form.Control type="text" placeholder="RUT sin DV, ej: 12345678" value={rut} onChange={(e) => setRut(e.target.value)} />
        <Button type="submit" variant="outline-primary" disabled={buscando}>{buscando ? <Spinner animation="border" size="sm" /> : "Buscar"}</Button>
      </Form>
      {noEncontrado && <p className="text-muted small mb-0">No está en el padrón de esta base (o no vigente, o fuera del ambiente que esta base admite — ver la guarda de ambiente).</p>}
      {resultado && (
        <Table size="sm" borderless className="mb-0">
          <tbody>
            <tr><td className="text-muted">RUT</td><td>{resultado.rut}-{resultado.dv}</td></tr>
            <tr><td className="text-muted">Razón social</td><td>{resultado.razon_social}{resultado.tiene_override && <Badge bg="info" className="ms-2">con override</Badge>}</td></tr>
            <tr><td className="text-muted">Mail intercambio</td><td>{resultado.mail_intercambio || <span className="text-muted">— ({resultado.mail_motivo || "sin dato"})</span>}{resultado.mail_es_override && <Badge bg="info" className="ms-2">override</Badge>}</td></tr>
            <tr><td className="text-muted">Vigente</td><td>{resultado.vigente ? <Badge bg="success">sí</Badge> : <Badge bg="secondary">no{resultado.baja_at ? ` (baja ${resultado.baja_at.slice(0, 10)})` : ""}</Badge>}</td></tr>
            <tr><td className="text-muted">Última actualización</td><td>{resultado.importado_at?.replace("T", " ").slice(0, 16)}</td></tr>
          </tbody>
        </Table>
      )}
    </>
  );
}

export default PadronSii;
